import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type CSSProperties,
} from 'react';
import moment from 'moment';
import { Button, DatePicker, Input, Tag } from 'antd';
import * as XLSX from 'xlsx';
import {
  ApartmentOutlined,
  CameraOutlined,
  ReloadOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  UserOutlined,
  UserSwitchOutlined,
  WalletOutlined,
  CalendarOutlined,
  ClearOutlined,
  CreditCardOutlined,
  FilePdfOutlined,
  FilterOutlined,
  IdcardOutlined,
  ShoppingOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import { FileSpreadsheet, FileText } from 'lucide-react';

import callApi, { Method } from '../../Api/config';
import {
  PAYMENT_VOUCHER_ACCOUNTING_STATUS_OPTIONS,
  PAYMENT_VOUCHER_METHOD_OPTIONS,
  PAYMENT_VOUCHER_STATUS_OPTIONS,
  getDocumitraApplicationStatusFilterOptions,
  documitraPaymentStatusFilterOption,
  statusOption as photoOrderStatus,
  partnerOrderStatusOption,
  REFUND_FEE_TYPE_OPTIONS,
  REFUND_REQUEST_PAYMENT_METHOD_OPTIONS,
  REFUND_STATUS_OPTIONS,
  Role,
} from '../../constants/AppConstant';
import { getRole } from '../../service/CookieManager';
import {
  DOCUMITRA_ORDER_APPLICATION,
  ORDER_USER,
  PARTNER_ORDERS,
  PAYMENT_VOUCHER,
  PHOTO_ORDER_APPLICATION,
  REFUND_REQUEST,
  APPLICATION_FORM_SERVICES,
  DOCUMITRA_ORDER_TRANSACTION,
  PHOTO_ORDER_TRANSACTION,
} from '../../constants/EntityName';
import { InvertFillButton } from '../../components/InvertFillButton';
import {
  downloadVoucherListExcel,
  downloadVoucherListPdf,
  fetchAllVouchers,
} from '../../components/PaymentVoucher/helpers';
import { toastError, toastSuccess } from '../../service/Toast';
import AbstractTabs from '../Entity/AbstractTabs';
import GenericTable from '../../components/GenericTable';
import Dropdown from '../../components/Dropdown';
import Search from '../../components/Search/Search';
import { downloadListData, downloadListDataPDF } from '../../components/DownloadData';

const { RangePicker } = DatePicker;

type ReportFormat = 'Excel' | 'PDF';

interface ReportDefinition {
  key: string;
  title: string;
  icon: ComponentType<{ style?: CSSProperties }>;
  iconColor: string;
  iconBackground: string;
  cardBackground: string;
}

interface DownloadHistoryItem {
  key: string;
  downloadedBy: string;
  report: string;
  format: ReportFormat;
  startDate: string;
  endDate: string;
  downloadedAt: string;
  filters: string;
  inspect: string;
}

const REPORTS: ReportDefinition[] = [
  {
    key: 'documitra-user',
    title: 'Documitra Users',
    icon: UserOutlined,
    iconColor: '#0369a1',
    iconBackground: '#e5f5ff',
    cardBackground: '#f7fcff',
  },
  {
    key: 'documitra-order',
    title: 'Documitra Order',
    icon: ShoppingCartOutlined,
    iconColor: '#0057c8',
    iconBackground: '#e8f1ff',
    cardBackground: '#f7faff',
  },
  {
    key: 'agent-order',
    title: 'Agent Order',
    icon: UserSwitchOutlined,
    iconColor: '#7c3aed',
    iconBackground: '#f1eaff',
    cardBackground: '#fcfaff',
  },
  {
    key: 'photo-order',
    title: 'Photo Order ',
    icon: CameraOutlined,
    iconColor: '#be185d',
    iconBackground: '#fde9f3',
    cardBackground: '#fff9fc',
  },
  {
    key: 'photomitra-transaction',
    title: 'Photomitra Transaction',
    icon: CameraOutlined,
    iconColor: '#7c3aed',
    iconBackground: '#f1eaff',
    cardBackground: '#fcfaff',
  },
  {
    key: 'documitra-transaction',
    title: 'Documitra Transaction',
    icon: CreditCardOutlined,
    iconColor: '#047857',
    iconBackground: '#e5f8f0',
    cardBackground: '#f7fffb',
  },
  {
    key: 'refund',
    title: 'Refund Request',
    icon: ReloadOutlined,
    iconColor: '#c2410c',
    iconBackground: '#fff0e8',
    cardBackground: '#fffaf7',
  },
  {
    key: 'payment-voucher',
    title: 'Payment Voucher ',
    icon: WalletOutlined,
    iconColor: '#047857',
    iconBackground: '#e5f8f0',
    cardBackground: '#f7fffb',
  },
];

/** Reports an Accountant may open and download. */
const ACCOUNTANT_REPORT_KEYS = ['refund', 'payment-voucher'];

const ORDER_REPORTS = REPORTS.filter(report =>
  ['documitra-user', 'documitra-order', 'agent-order', 'photo-order'].includes(report.key)
);
const TRANSACTION_REPORTS = REPORTS.filter(report =>
  ['photomitra-transaction', 'documitra-transaction', 'refund', 'payment-voucher'].includes(
    report.key
  )
);

const DOWNLOAD_HISTORY: DownloadHistoryItem[] = [
  {
    key: 'download-1',
    downloadedBy: 'Sarah Johnson',
    report: 'Documitra Order Report',
    format: 'Excel',
    startDate: '2026-09-01',
    endDate: '2026-09-14',
    downloadedAt: '2026-09-14T10:30:00',
    filters: 'Service: Apostille, Application Status: Completed',
    inspect: 'view',
  },
  {
    key: 'download-2',
    downloadedBy: 'Michael Smith',
    report: 'Documitra Order Report',
    format: 'PDF',
    startDate: '2026-09-01',
    endDate: '2026-09-13',
    downloadedAt: '2026-09-13T15:45:00',
    filters: 'Payment Status: Paid',
    inspect: 'view',
  },
  {
    key: 'download-3',
    downloadedBy: 'Sarah Johnson',
    report: 'Documitra Order Report',
    format: 'Excel',
    startDate: '2026-08-15',
    endDate: '2026-09-10',
    downloadedAt: '2026-09-11T09:15:00',
    filters: 'Service: Document Authentication',
    inspect: 'view',
  },
  {
    key: 'download-4',
    downloadedBy: 'David Wilson',
    report: 'Documitra Order Report',
    format: 'PDF',
    startDate: '2026-09-01',
    endDate: '2026-09-08',
    downloadedAt: '2026-09-08T12:00:00',
    filters: 'Application Status: Under Review',
    inspect: 'view',
  },
];

const PaymentVoucherReportFilters = () => {
  const [vouchers, setVouchers] = useState<any[]>([]);
  const [matchCount, setMatchCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [status, setStatus] = useState('All');
  const [paymentMethod, setPaymentMethod] = useState('All');
  const [accountingStatus, setAccountingStatus] = useState('All');
  const [range, setRange] = useState<any>(null);

  // Preview only — one page is enough to know whether anything matches and how
  // many rows the download will contain. The date range is applied on the
  // server against each voucher's status date (settlement / cancellation /
  // last payment date), not its created date.
  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchAllVouchers({
        status,
        paymentMethod,
        accountingStatus,
        range,
        maxPages: 1,
      });
      if (!result.ok) {
        setVouchers([]);
        setMatchCount(0);
        toastError(result.message || 'Failed to fetch voucher report');
        return;
      }
      setVouchers(result.rows);
      setMatchCount(result.totalCount);
    } catch (error) {
      setVouchers([]);
      setMatchCount(0);
      toastError('Failed to fetch voucher report');
    } finally {
      setLoading(false);
    }
  }, [accountingStatus, paymentMethod, range, status]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  /** Every matching voucher, not just the previewed page. */
  const collectRows = async (): Promise<any[] | null> => {
    if (vouchers.length >= matchCount) return vouchers;
    const result = await fetchAllVouchers({
      status,
      paymentMethod,
      accountingStatus,
      range,
    });
    if (!result.ok) {
      toastError(result.message || 'Failed to fetch voucher report');
      return null;
    }
    if (result.truncated) {
      toastError(
        `Only the first ${result.rows.length} of ${result.totalCount} vouchers could be downloaded. Narrow the date range to get the rest.`
      );
    }
    return result.rows;
  };

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const vouchersToExport = await collectRows();
      if (!vouchersToExport?.length) return;
      downloadVoucherListExcel(vouchersToExport);
      toastSuccess('Excel report downloaded');
    } catch (error) {
      toastError('Failed to download Excel report');
    } finally {
      setDownloading(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const vouchersToExport = await collectRows();
      if (!vouchersToExport?.length) return;
      downloadVoucherListPdf(vouchersToExport);
      toastSuccess('PDF report downloaded');
    } catch (error) {
      toastError('Failed to download PDF report');
    } finally {
      setDownloading(false);
    }
  };

  const statusOptions = [
    { id: 'all-statuses', name: 'All Status', value: 'All' },
    ...PAYMENT_VOUCHER_STATUS_OPTIONS,
  ];
  const paymentMethodOptions = [
    { id: 'all-payment-methods', name: 'All Payment Methods', value: 'All' },
    ...PAYMENT_VOUCHER_METHOD_OPTIONS,
  ];
  const accountingStatusOptions = [
    { id: 'all-accounting-statuses', name: 'All Accounting Status', value: 'All' },
    ...PAYMENT_VOUCHER_ACCOUNTING_STATUS_OPTIONS,
  ];

  const clearFilters = () => {
    setRange(null);
    setStatus('All');
    setPaymentMethod('All');
    setAccountingStatus('All');
  };

  return (
    <div style={{ minHeight: 'calc(100vh - 72px)', padding: '24px 20px' }}>
      <h1 style={{ color: '#071b41', fontSize: 22, margin: '0 0 16px' }}>Payment Voucher Report</h1>
      <p style={{ color: '#58709b', margin: '-8px 0 20px' }}>
        Select a date range and filters, then download the payment voucher report. The date range is
        matched against each voucher&apos;s status date — settlement date for Settled, cancellation
        date for Cancelled, last payment date for Partially Paid, and creation date for Generated.
      </p>
      <div
        style={{
          border: '1px solid #dbe4f3',
          borderRadius: 12,
          background: '#fff',
          padding: 22,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 18,
          }}
        >
          <strong style={{ color: '#071b41', fontSize: 15 }}>Download reports:</strong>
          <InvertFillButton
            variant="red"
            fontSize="base"
            fontWeight="semibold"
            onClick={clearFilters}
          >
            <ClearOutlined />
            Clear filters
          </InvertFillButton>
        </div>
        <div
          style={{
            borderTop: '1px solid #e7edf5',
            paddingTop: 18,
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: '18px 14px',
          }}
        >
          <div>
            <div style={{ color: '#344767', fontSize: 12, fontWeight: 600, marginBottom: 7 }}>
              <CalendarOutlined style={reportLabelIconStyle} /> Status date range
            </div>
            <RangePicker
              value={range}
              onChange={setRange}
              format="DD-MM-YYYY"
              style={reportFieldStyle}
            />
          </div>
          <div>
            <div style={{ color: '#344767', fontSize: 12, fontWeight: 600, marginBottom: 7 }}>
              <FilterOutlined style={reportLabelIconStyle} /> Voucher status
            </div>
            <Dropdown
              filterName="Select voucher status"
              filterKey="status"
              selectedValue={status === 'All' ? undefined : status}
              checkBoxOptions={statusOptions}
              onSubmitFilter={(_, value) => setStatus(value || 'All')}
              style={reportFieldStyle}
              width="100%"
            />
          </div>
          <div>
            <div style={{ color: '#344767', fontSize: 12, fontWeight: 600, marginBottom: 7 }}>
              <CreditCardOutlined style={reportLabelIconStyle} /> Payment method
            </div>
            <Dropdown
              filterName="Select payment method"
              filterKey="paymentMethod"
              selectedValue={paymentMethod === 'All' ? undefined : paymentMethod}
              checkBoxOptions={paymentMethodOptions}
              onSubmitFilter={(_, value) => setPaymentMethod(value || 'All')}
              style={reportFieldStyle}
              width="100%"
            />
          </div>
          <div>
            <div style={{ color: '#344767', fontSize: 12, fontWeight: 600, marginBottom: 7 }}>
              <TagsOutlined style={reportLabelIconStyle} /> Accounting Status
            </div>
            <Dropdown
              filterName="Select accounting status"
              filterKey="accountingStatus"
              selectedValue={accountingStatus === 'All' ? undefined : accountingStatus}
              checkBoxOptions={accountingStatusOptions}
              onSubmitFilter={(_, value) => setAccountingStatus(value || 'All')}
              style={reportFieldStyle}
              width="100%"
            />
          </div>
        </div>
        <div
          style={{
            borderTop: '1px solid #e7edf5',
            marginTop: 20,
            paddingTop: 16,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{ color: '#58709b', fontSize: 12, marginRight: 'auto' }}>
            {loading && 'Loading vouchers…'}
            {!loading && downloading && 'Preparing download — fetching all matching vouchers…'}
            {!loading &&
              !downloading &&
              `${matchCount} voucher${matchCount === 1 ? '' : 's'} match the selected filters`}
          </span>
          <InvertFillButton
            variant="white"
            fontSize="base"
            fontWeight="semibold"
            onClick={downloadPdf}
            disabled={loading || downloading || !matchCount}
          >
            <FilePdfOutlined />
            Download PDF
          </InvertFillButton>
          <InvertFillButton
            variant="blue"
            fontSize="base"
            fontWeight="bold"
            onClick={downloadExcel}
            disabled={loading || downloading || !matchCount}
          >
            <FileSpreadsheet size={16} />
            Download Excel
          </InvertFillButton>
        </div>
      </div>
    </div>
  );
};

type ReportFilter = {
  key: string;
  label: string;
  icon: ComponentType<{ style?: CSSProperties }>;
  type: 'date' | 'select' | 'input';
  options?: any[];
  multiple?: boolean;
};

const reportFieldStyle = {
  width: '100%',
  height: 40,
  borderRadius: 8,
  background: '#f8fafc',
  border: '1px solid #dbe3ef',
};

const reportLabelIconStyle = {
  background: '#e8f1ff',
  border: '1px solid #c9dcfa',
  borderRadius: 4,
  color: '#3569a8',
  fontSize: 12,
  marginRight: 6,
  padding: 4,
};

const ReportFilterPanel = ({ report }: { report: ReportDefinition }) => {
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [services, setServices] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const requests: Promise<void>[] = [];

    if (
      ['documitra-order', 'agent-order', 'documitra-transaction', 'refund'].includes(report.key)
    ) {
      requests.push(
        callApi(APPLICATION_FORM_SERVICES, 'GET', null, { page: '1', limit: '100' })
          .then(res => {
            const results = res?.data?.data?.results || [];
            setServices(
              [...results].reverse().map((item: any) => ({
                id: item.id,
                value: item.name,
                name: item.name,
              }))
            );
          })
          .catch(() => toastError('Failed to load service filters'))
      );
    }

    if (report.key === 'documitra-transaction') {
      requests.push(
        callApi('admin/countries', 'GET')
          .then(res => {
            setCountries(
              (res?.data?.data || []).map((country: string) => ({
                id: country,
                value: country,
                name: country,
              }))
            );
          })
          .catch(() => toastError('Failed to load country filters'))
      );
    }

    void Promise.all(requests);
  }, [report.key]);

  const filtersForReport = (): ReportFilter[] => {
    const date = {
      key: 'date',
      label: 'Date range',
      icon: CalendarOutlined,
      type: 'date' as const,
    };
    if (report.key === 'agent-order') {
      return [
        date,
        {
          key: 'memberStatus',
          label: 'Application status',
          icon: FilterOutlined,
          type: 'select',
          options: partnerOrderStatusOption,
        },
        {
          key: 'plan',
          label: 'Plan name',
          icon: ShoppingOutlined,
          type: 'select',
          options: [
            { id: '1', name: 'Online Consultation', value: 'Online Consultation' },
            { id: '2', name: 'Ready to Ship', value: 'Ready to Ship' },
          ],
        },
      ];
    }
    if (report.key === 'refund') {
      return [
        date,
        {
          key: 'status',
          label: 'Refund status',
          icon: FilterOutlined,
          type: 'select',
          multiple: true,
          options: REFUND_STATUS_OPTIONS,
        },
        {
          key: 'refundType',
          label: 'Fee type',
          icon: TagsOutlined,
          type: 'select',
          multiple: true,
          options: REFUND_FEE_TYPE_OPTIONS,
        },
        {
          key: 'applicationType',
          label: 'Service',
          icon: TagsOutlined,
          type: 'select',
          multiple: true,
          options: services,
        },
        {
          key: 'paymentMethod',
          label: 'Payment method',
          icon: CreditCardOutlined,
          type: 'select',
          multiple: true,
          options: REFUND_REQUEST_PAYMENT_METHOD_OPTIONS,
        },
      ];
    }
    if (report.key === 'photo-order') {
      return [
        date,
        {
          key: 'status',
          label: 'Order status',
          icon: FilterOutlined,
          type: 'select',
          options: photoOrderStatus,
        },
        {
          key: 'paymentStatus',
          label: 'Payment status',
          icon: CreditCardOutlined,
          type: 'select',
          options: documitraPaymentStatusFilterOption,
        },
      ];
    }
    if (report.key === 'documitra-user') {
      return [
        date,
        {
          key: 'active',
          label: 'Account status',
          icon: FilterOutlined,
          type: 'select',
          options: [
            { id: '1', value: 'true', name: 'Active' },
            { id: '2', value: 'false', name: 'Inactive' },
          ],
        },
        {
          key: 'source',
          label: 'Source',
          icon: IdcardOutlined,
          type: 'select',
          options: [
            { id: '1', value: 'documitra', name: 'Documitra' },
            { id: '2', value: 'referral', name: 'Partners' },
          ],
        },
        {
          key: 'orderActivity',
          label: 'Order activity',
          icon: ShoppingOutlined,
          type: 'select',
          options: [
            { id: '1', value: 'created', name: 'Order Created' },
            { id: '2', value: 'registeredOnly', name: 'Registered Only (No Orders)' },
          ],
        },
      ];
    }
    if (report.key === 'photomitra-transaction') {
      return [
        date,
        {
          key: 'paymentStatus',
          label: 'Payment status',
          icon: CreditCardOutlined,
          type: 'select',
          options: documitraPaymentStatusFilterOption,
        },
      ];
    }
    if (report.key === 'documitra-transaction') {
      return [
        date,
        {
          key: 'paymentStatus',
          label: 'Payment status',
          icon: CreditCardOutlined,
          type: 'select',
          options: documitraPaymentStatusFilterOption,
        },
        {
          key: 'serviceName',
          label: 'Service',
          icon: TagsOutlined,
          type: 'select',
          options: services,
        },
        {
          key: 'applyingFrom',
          label: 'Country (Applying From)',
          icon: IdcardOutlined,
          type: 'select',
          options: countries,
        },
      ];
    }
    return [
      date,
      {
        key: 'serviceName',
        label: 'Service',
        icon: TagsOutlined,
        type: 'select',
        options: services,
      },
      {
        key: 'memberStatus',
        label: 'Application status',
        icon: FilterOutlined,
        type: 'select',
        options: getDocumitraApplicationStatusFilterOptions(),
      },
      {
        key: 'paymentStatus',
        label: 'Payment status',
        icon: CreditCardOutlined,
        type: 'select',
        options: documitraPaymentStatusFilterOption,
      },
      {
        key: 'plan',
        label: 'Plan',
        icon: ShoppingOutlined,
        type: 'select',
        options: [
          { id: '1', name: 'Online Consultation', value: 'Online Consultation' },
          { id: '2', name: 'Ready to Ship', value: 'Ready to Ship' },
        ],
      },
      {
        key: 'addOns',
        label: 'Add ons',
        icon: TagsOutlined,
        type: 'select',
        multiple: true,
        options: [
          { id: '1', name: 'Expedite', value: 'expedite' },
          { id: '2', name: 'Photo Enhancement', value: 'photoEnhancement' },
          { id: '3', name: 'Apostilled', value: 'apostilled' },
          { id: '4', name: 'Courier Service', value: 'courierService' },
        ],
      },
      {
        key: 'rejectedApplications',
        label: 'Rejected applications',
        icon: FilterOutlined,
        type: 'select',
        options: [{ id: '1', value: 'true', name: 'Rejected applications only' }],
      },
      { key: 'partnerId', label: 'Referral partner ID', icon: IdcardOutlined, type: 'input' },
    ];
  };

  const reportType =
    report.key === 'agent-order'
      ? 'PartnerOrder'
      : report.key === 'refund'
        ? 'RefundRequest'
        : report.key === 'photo-order'
          ? 'PhotomitraOrder'
          : report.key === 'documitra-user'
            ? 'Users'
            : report.key === 'photomitra-transaction'
              ? 'PhotomitraTransaction'
              : report.key === 'documitra-transaction'
                ? 'DocumitraTransaction'
                : 'DocumitraOrder';
  const reportFilters = filtersForReport();
  const setFilter = (key: string, value: any) =>
    setFilters(previous => ({ ...previous, [key]: value }));
  const clearFilters = () => setFilters({});

  const download = async (isPdf: boolean) => {
    const date = filters.date;
    if (!date?.[0] || !date?.[1]) {
      toastError('Please select a date range first.');
      return;
    }
    try {
      setDownloading(true);
      const params = {
        startDate: date[0].format('YYYY-MM-DD'),
        endDate: date[1].format('YYYY-MM-DD'),
        reportType,
        // Multi-selects go as comma-separated strings (the report DTO validates strings).
        ...Object.fromEntries(
          Object.entries(filters)
            .filter(
              ([key, value]) =>
                key !== 'date' &&
                value != null &&
                value !== '' &&
                !(Array.isArray(value) && value.length === 0)
            )
            .map(([key, value]) => [key, Array.isArray(value) ? value.join(',') : value])
        ),
      };
      // Refund report has its own endpoint (Super Admin + Accountant); same rows.
      const isRefundReport = report.key === 'refund';
      const response = await callApi(
        isRefundReport ? '/v1/refund/report/download' : '/admin/download-reports',
        'GET',
        null,
        isRefundReport ? { ...params, reportType: undefined } : params,
        {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        Method.DOWNLOAD
      );
      if (response?.statusCode >= 400 || !response?.status || !response?.data) {
        toastError(response?.data?.message || 'Failed to download report');
        return;
      }
      isPdf
        ? downloadListDataPDF(response, reportEntityName(report.key))
        : downloadListData(response, reportEntityName(report.key));
      toastSuccess(`${isPdf ? 'PDF' : 'Excel'} report downloaded`);
    } catch (error) {
      toastError('Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={{ border: '1px solid #dbe3ef', borderRadius: 12, background: '#fff', padding: 22 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 18,
        }}
      >
        <strong style={{ color: '#071b41', fontSize: 15 }}>Download reports:</strong>
        <InvertFillButton
          variant="red"
          fontSize="base"
          fontWeight="semibold"
          onClick={clearFilters}
        >
          <ClearOutlined />
          Clear filters
        </InvertFillButton>
      </div>
      <div
        style={{
          borderTop: '1px solid #e7edf5',
          paddingTop: 18,
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: '18px 14px',
        }}
      >
        {reportFilters.map(filter => {
          const Icon = filter.icon;
          return (
            <div key={filter.key} style={{ minWidth: 0 }}>
              <div style={{ color: '#344767', fontSize: 12, fontWeight: 600, marginBottom: 7 }}>
                <Icon style={reportLabelIconStyle} /> {filter.label}
              </div>
              {filter.type === 'date' ? (
                <RangePicker
                  value={filters.date || null}
                  onChange={value => setFilter('date', value)}
                  style={reportFieldStyle}
                />
              ) : filter.type === 'input' ? (
                <Input
                  value={filters[filter.key] || ''}
                  onChange={event => setFilter(filter.key, event.target.value)}
                  style={reportFieldStyle}
                />
              ) : (
                <Dropdown
                  filterName={`Select ${filter.label}`}
                  filterKey={filter.key}
                  selectedValue={filters[filter.key]}
                  allowMultiple={filter.multiple}
                  checkBoxOptions={filter.options || []}
                  onSubmitFilter={setFilter}
                  style={reportFieldStyle}
                  width="100%"
                />
              )}
            </div>
          );
        })}
      </div>
      <div
        style={{
          borderTop: '1px solid #e7edf5',
          marginTop: 20,
          paddingTop: 16,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
        }}
      >
        <InvertFillButton
          variant="white"
          fontSize="base"
          fontWeight="semibold"
          onClick={() => download(true)}
          disabled={downloading}
        >
          <FilePdfOutlined />
          Download PDF
        </InvertFillButton>
        <InvertFillButton
          variant="blue"
          fontSize="base"
          fontWeight="bold"
          onClick={() => download(false)}
          disabled={downloading}
        >
          <FileSpreadsheet size={16} />
          Download Excel
        </InvertFillButton>
      </div>
    </div>
  );
};

const reportEntityName = (key: string) =>
  key === 'agent-order'
    ? PARTNER_ORDERS
    : key === 'refund'
      ? REFUND_REQUEST
      : key === 'photo-order'
        ? PHOTO_ORDER_APPLICATION
        : key === 'documitra-user'
          ? ORDER_USER
          : key === 'photomitra-transaction'
            ? PHOTO_ORDER_TRANSACTION
            : key === 'documitra-transaction'
              ? DOCUMITRA_ORDER_TRANSACTION
              : DOCUMITRA_ORDER_APPLICATION;

const ReportDownloadTab = ({ report }: { report: ReportDefinition }) => {
  return (
    <div style={{ minHeight: 'calc(100vh - 72px)', padding: '24px 20px' }}>
      <h1 style={{ color: '#071b41', fontSize: 22, margin: '0 0 16px' }}>{report.title}</h1>
      <p style={{ color: '#58709b', margin: '-8px 0 20px' }}>
        Pick a report to apply filters and download, or check past downloads in the log below.
      </p>
      <ReportFilterPanel report={report} />
    </div>
  );
};

const DocumitraOrderReport = () => {
  const isAccountant = getRole() === Role.ACCOUNTANT;
  const visibleOrderReports = isAccountant ? [] : ORDER_REPORTS;
  const visibleTransactionReports = isAccountant
    ? TRANSACTION_REPORTS.filter(report => ACCOUNTANT_REPORT_KEYS.includes(report.key))
    : TRANSACTION_REPORTS;
  const visibleReports = [...visibleOrderReports, ...visibleTransactionReports];
  const [activeKey, setActiveKey] = useState('0');
  const [openReports, setOpenReports] = useState<ReportDefinition[]>([]);
  const [search, setSearch] = useState('');
  const [reportType, setReportType] = useState('All Report Types');
  const [format, setFormat] = useState('All Formats');
  const [dateRange, setDateRange] = useState<[moment.Moment | null, moment.Moment | null]>([
    null,
    null,
  ]);

  const filteredHistory = useMemo(
    () =>
      DOWNLOAD_HISTORY.filter(item => {
        const searchable = `${item.report} ${item.downloadedBy} ${item.filters}`.toLowerCase();
        const matchesSearch = searchable.includes(search.toLowerCase());
        const matchesReport = reportType === 'All Report Types' || item.report === reportType;
        const matchesFormat = format === 'All Formats' || item.format === format;
        const downloadedAt = moment(item.downloadedAt);
        const matchesDate =
          (!dateRange[0] || downloadedAt.isSameOrAfter(dateRange[0], 'day')) &&
          (!dateRange[1] || downloadedAt.isSameOrBefore(dateRange[1], 'day'));
        return matchesSearch && matchesReport && matchesFormat && matchesDate;
      }),
    [dateRange, format, reportType, search]
  );

  const openReport = (report: ReportDefinition) => {
    setOpenReports(previous => {
      if (previous.some(openReportItem => openReportItem.key === report.key)) {
        return previous;
      }
      return [...previous, report];
    });
    setActiveKey(report.key);
  };

  const renderReportCards = (reports: ReportDefinition[]) => (
    <div
      style={{
        display: 'grid',
        gap: 14,
        gridTemplateColumns: 'repeat(4, minmax(130px, 1fr))',
      }}
    >
      {reports.map(report => {
        const ReportIcon = report.icon;
        return (
          <button
            key={report.key}
            onClick={() => openReport(report)}
            style={{
              background: '#ffffff',
              border: '1px solid #dbe3ef',
              borderRadius: 12,
              boxShadow: '0 2px 8px rgba(31, 41, 55, 0.04)',
              cursor: 'pointer',
              padding: '20px 10px',
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              justifyContent: 'center',
            }}
            type="button"
          >
            <ReportIcon
              style={{
                background: report.iconBackground,
                borderRadius: '50%',
                color: report.iconColor,
                fontSize: 24,
                padding: 10,
              }}
            />
            <strong style={{ color: '#071b41', display: 'block', fontSize: 13 }}>
              {report.title}
            </strong>
          </button>
        );
      })}
    </div>
  );

  const mainContent = (
    <div style={{ minHeight: 'calc(100vh - 72px)', padding: '24px 27px' }}>
      <h1 style={{ color: '#071b41', fontSize: 22, margin: 0 }}>Documitra Reports</h1>
      <p style={{ color: '#58709b', margin: '8px 0 20px' }}>
        Pick a report to apply filters and download, or check past downloads in the log below.
      </p>
      <div
        style={{
          backdropFilter: 'blur(4px)',
          padding: 20,
          borderRadius: 12,
          background: '#f5f7fb',
          border: '1px solid #dbe3ef',
        }}
      >
        {visibleOrderReports.length > 0 && renderReportCards(visibleOrderReports)}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            margin: '24px 0 20px',
          }}
        >
          <strong style={{ color: '#004195', fontSize: 14, whiteSpace: 'nowrap' }}>
            Transaction Reports
          </strong>
          <div style={{ flex: 1, height: 1, background: '#dbe3ef' }} />
        </div>
        {renderReportCards(visibleTransactionReports)}
      </div>

      {/* <h2 style={{ color: '#071b41', fontSize: 16, margin: '28px 0 12px' }}>
        Download Log — All Reports
      </h2>
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          marginBottom: 16,
        }}
      >
        <div style={{ flex: '1 1 340px', maxWidth: 420 }}>
          <Search
            setSearchQuery={setSearch}
            placeholderValue="file name"
            leftIconImg
            fullWidth
            style={{
              background: '#fff',
              color: '#1F2937',
            }}
          />
        </div>
        <Dropdown
          filterName="All Report Types"
          filterKey="reportType"
          selectedValue={reportType === 'All Report Types' ? undefined : reportType}
          checkBoxOptions={['All Report Types', ...visibleReports.map(report => report.title)].map(
            value => ({ name: value, value })
          )}
          onSubmitFilter={(_, value) => setReportType(value || 'All Report Types')}
          width="170px"
          style={{
            background: '#fff',
            color: '#1F2937',
            borderColor: '#dbe3ef',
          }}
        />
        <Dropdown
          filterName="All Formats"
          filterKey="format"
          selectedValue={format === 'All Formats' ? undefined : format}
          checkBoxOptions={['All Formats', 'Excel', 'PDF'].map(value => ({
            name: value,
            value,
          }))}
          onSubmitFilter={(_, value) => setFormat(value || 'All Formats')}
          width="150px"
          style={{
            background: '#fff',
            color: '#1F2937',
            borderColor: '#dbe3ef',
          }}
        />
        <RangePicker
          format="DD-MM-YYYY"
          value={dateRange}
          onChange={dates => setDateRange(dates || [null, null])}
          style={{
            height: '38px',
            borderRadius: '8px',
            background: '#fff',
            border: '1px solid #dbe3ef',
            color: '#1F2937',
            minWidth: '230px',
          }}
        />
        <Button
          onClick={() => {
            setSearch('');
            setReportType('All Report Types');
            setFormat('All Formats');
            setDateRange([null, null]);
          }}
          style={{
            height: '38px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: '#1F2937',
            borderColor: '#dbe3ef',
            background: '#fff',
            fontWeight: 500,
          }}
        >
          <span style={{ fontSize: '12px' }}>✕</span>
          Clear
        </Button>
      </div>
      <GenericTable<DownloadHistoryItem>
        rowKey="key"
        bordered
        dataSource={filteredHistory}
        columns={[
          { title: 'Report Type', dataIndex: 'report' },
          {
            title: 'Format',
            dataIndex: 'format',
            render: (value: ReportFormat) => (
              <Tag
                color={value === 'Excel' ? 'green' : value === 'PDF' ? 'red' : 'blue'}
                icon={value === 'Excel' ? <FileSpreadsheet size={14} /> : <FileText size={14} />}
              >
                {value}
              </Tag>
            ),
          },

          {
            title: 'Start Date',
            dataIndex: 'startDate',
            render: (value: string) => moment(value).format('DD-MM-YYYY'),
          },
          {
            title: 'End Date',
            dataIndex: 'endDate',
            render: (value: string) => moment(value).format('DD-MM-YYYY'),
          },
          { title: 'Downloaded By', dataIndex: 'downloadedBy' },
          {
            title: 'Created On',
            dataIndex: 'downloadedAt',
            render: (value: string) => moment(value).format('DD-MM-YYYY hh:mm A'),
          },

          {
            title: 'Actions',
            dataIndex: 'inspect',
            align: 'center',
            render: () => (
              <a href="#report-download" style={{ color: '#1677ff' }}>
                View
              </a>
            ),
          },
        ]}
        pagination={{ pageSize: 10, showSizeChanger: false }}
      /> */}
    </div>
  );

  return (
    <AbstractTabs
      activeKey={activeKey}
      entityName="Reports"
      tabCount={visibleReports.length}
      tabCountText="Report Types"
      updateFormVisibility={(action, targetKey) => {
        if (action !== 'remove' || typeof targetKey !== 'string') return;
        setOpenReports(previous => {
          const remainingReports = previous.filter(report => report.key !== targetKey);
          if (activeKey === targetKey) {
            setActiveKey(remainingReports[remainingReports.length - 1]?.key || '0');
          }
          return remainingReports;
        });
      }}
      onTabClick={key => setActiveKey(key)}
      content={
        openReports.length
          ? openReports.map(report => ({
              key: report.key,
              tabTitle: report.title,
              content:
                report.key === 'payment-voucher' ? (
                  <PaymentVoucherReportFilters />
                ) : (
                  <ReportDownloadTab report={report} />
                ),
            }))
          : undefined
      }
    >
      {mainContent}
    </AbstractTabs>
  );
};

export default DocumitraOrderReport;
